import React, { useRef, useState } from "react";
import {
  lockedProps,
  type GateProps,
} from "../../billing/hooks/useSubscriptionGate";

export interface PlanNameFieldProps {
  name: string;
  /**
   * Whether the name can be changed at all. Off once the plan has gone to the
   * client: what they were sent is what it is called, and a heading that
   * invited an edit it would then refuse is worse than one that does not.
   */
  editable: boolean;
  /**
   * Whether the workspace may write this plan at all — see
   * `useSubscriptionGate`. Unlike `editable` above the heading stays a button:
   * a sent plan's name is settled and there is nothing to say about it, while a
   * lapsed subscription is a thing to put right, and the press is where that
   * gets said.
   */
  gate?: GateProps;
  /**
   * Stores the new name. Answers `false` if it was not stored — the backend
   * refused it — which is what keeps the box open on what was typed rather
   * than closing on a name the plan does not have.
   */
  onRename: (name: string) => Promise<boolean>;
}

/**
 * The plan's name in the page heading, changed where it is read.
 *
 * A name is one field, and a dialog to edit one field is a heavier thing than
 * what it protects — the same reasoning the option's delete is answered in
 * place rather than in a modal. So the heading is the control: click it and
 * it becomes a box holding what it said, Enter or clicking away stores it,
 * Escape leaves it as it was.
 *
 * It is a button rather than a span with a click on it, so it can be reached
 * by keyboard like everything else that does something, and the dashed
 * underline appears under the pointer to say that it will.
 */
const PlanNameField: React.FC<PlanNameFieldProps> = ({
  name,
  editable,
  gate = { disabled: false },
  onRename,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * Whether a key has already settled what happens to what was typed.
   *
   * Enter and Escape each answer on their own, and letting go of a box that
   * one of them has just closed would otherwise answer a second time. A ref
   * rather than state: it is read in the blur the same keystroke causes,
   * which is before a re-render could carry it.
   */
  const decided = useRef(false);

  if (!editable) return <>{name}</>;

  const start = () => {
    setValue(name);
    setEditing(true);
    // Selected rather than left with a caret at one end: a name is usually
    // replaced outright, and typing over it is then the first keystroke.
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const store = async () => {
    const next = value.trim();
    // Nothing typed, or nothing changed — closed without a word to the
    // server, since there is nothing for it to store.
    if (!next || next === name) {
      setEditing(false);
      return;
    }

    setSaving(true);
    const stored = await onRename(next);
    setSaving(false);
    if (stored) setEditing(false);
    else inputRef.current?.focus();
  };

  if (!editing) {
    return (
      <button
        type="button"
        {...lockedProps(gate, start)}
        title={gate.title ?? "Rename this plan"}
        // Not dimmed the way the other locked controls are: this is the plan's
        // own name in the page heading, and greying it would read as the plan
        // being off rather than the button. What goes is the dashed underline
        // that offers the edit, which is the whole of what the control was.
        className={`truncate rounded-sm underline decoration-dashed decoration-transparent underline-offset-4 transition-colors focus:outline-none ${
          gate.disabled
            ? "cursor-not-allowed"
            : "hover:decoration-brand focus-visible:decoration-brand"
        }`}
      >
        {name}
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      aria-label="Plan name"
      disabled={saving}
      onChange={(event) => {
        // Typing again is a new answer waiting to be given, whatever the last
        // key settled.
        decided.current = false;
        setValue(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          decided.current = true;
          store();
        }
        if (event.key === "Escape") {
          decided.current = true;
          setEditing(false);
        }
      }}
      // Clicking away stores it, the same as Enter — a name typed and left
      // is a name meant, and nothing is lost by taking it.
      onBlur={() => {
        if (decided.current) {
          decided.current = false;
          return;
        }
        store();
      }}
      // Sized to what it holds, so the heading neither jumps to a full-width
      // box nor hides the end of a long name. Bounded on both sides: a name
      // of two letters is still something to aim at, and a very long one
      // stops before it pushes the plan's number off the row.
      style={{ width: `${Math.min(Math.max(value.length + 1, 8), 40)}ch` }}
      className="min-w-0 max-w-full border-b-2 border-brand bg-transparent text-ink outline-none disabled:opacity-60"
    />
  );
};

export default PlanNameField;
